const APP_TELEMETRY_ENABLED = process.env.REACT_APP_TELEMETRY_ENABLED
const APP_ENV = process.env.REACT_APP_ENV || 'development'

function getSeverityNumber(severityText: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR') {
  const severityMap = {
    TRACE: 1,
    DEBUG: 5,
    INFO: 9,
    WARN: 13,
    ERROR: 17,
    FATAL: 21
  }
  return severityMap[severityText] || 0
}

function getBrowserInfos() {
  const userAgent =
    typeof window !== 'undefined' && window.navigator
      ? window.navigator.userAgent
      : undefined
  const language =
    typeof window !== 'undefined' && window.navigator
      ? window.navigator.language
      : undefined
  const platform =
    typeof window !== 'undefined' && window.navigator
      ? window.navigator.platform
      : undefined
  const screenWidth =
    typeof window !== 'undefined' && window.screen
      ? window.screen.width
      : undefined
  const screenHeight =
    typeof window !== 'undefined' && window.screen
      ? window.screen.height
      : undefined
  const viewportWidth =
    typeof window !== 'undefined' && window.innerWidth
      ? window.innerWidth
      : undefined
  const viewportHeight =
    typeof window !== 'undefined' && window.innerHeight
      ? window.innerHeight
      : undefined
  const referrer =
    typeof document !== 'undefined' && document.referrer
      ? document.referrer
      : undefined
  const url =
    typeof window !== 'undefined' && window.location
      ? window.location.href
      : undefined

  const browserInfos = JSON.stringify({
    userAgent,
    language,
    platform,
    screenWidth,
    screenHeight,
    viewportWidth,
    viewportHeight,
    referrer,
    url
  })
  return browserInfos
}
const isTelemetryEnabled = () => APP_TELEMETRY_ENABLED === 'true'

const getErrorBody = (error: Error) => {
  const extra: Record<string, unknown> = {}
  if (error && typeof error === 'object') {
    Object.getOwnPropertyNames(error).forEach(key => {
      if (!['name', 'message', 'stack'].includes(key)) {
        extra[key] = (error as unknown as Record<string, unknown>)[key]
      }
    })
  }
  return JSON.stringify({
    name: error?.name,
    message: error?.message,
    stack: error?.stack,
    ...extra
  })
}

let hasWarnedHashFailure = false

const getStackTraceHash = async (error?: Error) => {
  if (!error?.stack || !globalThis.crypto?.subtle) return undefined
  try {
    const digest = await globalThis.crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(error.stack)
    )
    return [...new Uint8Array(digest)]
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  } catch (e) {
    if (!hasWarnedHashFailure) {
      hasWarnedHashFailure = true
      console.warn('Failed to compute vtex.stack_trace.hash:', e)
    }
    return undefined
  }
}

const getErrorAttributes = async (error?: Error) => {
  const attributes: ResourceAttribute[] = []
  if (!error) return attributes
  const add = (key: string, value?: unknown) => {
    if (value !== undefined && value !== null && value !== '') {
      attributes.push({ key, value: { stringValue: String(value) } })
    }
  }
  const { code, response } = error as Error & {
    code?: string | number
    response?: { status?: number }
  }
  add('vtex.stack_trace.hash', await getStackTraceHash(error))
  add('vtex.exception.type', error.name)
  add('vtex.exception.message', error.message)
  add('exception.stacktrace', error.stack)
  add('code', code ?? response?.status)
  return attributes
}

export function generateSpanId(): string {
  return [...crypto.getRandomValues(new Uint8Array(8))]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export function generateTraceId(): string {
  return [...crypto.getRandomValues(new Uint8Array(16))]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

function createTelemetryClient() {
  const collectorEndpoint = 'https://stable.vtexobservability.com'
  const searchIndex = 'ads_pac'
  const hostname =
    typeof window !== 'undefined' && window.location
      ? window.location.hostname
      : undefined
  const environment = APP_ENV
  const path =
    typeof window !== 'undefined' && window.location
      ? window.location.pathname
      : undefined
  const browserInfos = getBrowserInfos()
  const searchParams =
    typeof window !== 'undefined' && window.location
      ? window.location.search
      : undefined
  let traceId = generateTraceId()

  async function log(options: LogOptions): Promise<void> {
    const currentTime = Date.now() * 1000000
    const {
      message,
      body: _body,
      bodyError,
      severityText,
      severityNumber,
      traceId: _traceId,
      spanId: _spanId
    } = options

    const spanId = _spanId || generateSpanId()

    if (_traceId) {
      traceId = _traceId
    }
    const currentTraceId = traceId

    const body = bodyError ? getErrorBody(bodyError) : _body
    const errorAttributes = await getErrorAttributes(bodyError)

    const data = {
      resourceLogs: [
        {
          resource: {
            attributes: [
              {
                key: '_msg',
                value: {
                  stringValue: message || 'Unexpected error from PAC'
                }
              },
              {
                key: 'service.name',
                value: {
                  stringValue: 'newtail-admin'
                }
              },
              {
                key: 'service.version',
                value: {
                  stringValue: process.env.VERSION || 'unknown'
                }
              },
              {
                key: 'vtex.application.id',
                value: {
                  stringValue: 'newtail-admin'
                }
              },
              {
                key: 'hostname',
                value: {
                  stringValue: hostname
                }
              },
              {
                key: 'path',
                value: {
                  stringValue: path
                }
              },
              {
                key: 'environment',
                value: {
                  stringValue: environment
                }
              },
              {
                key: 'browserInfos',
                value: {
                  stringValue: browserInfos
                }
              },
              {
                key: 'searchParams',
                value: {
                  stringValue: searchParams
                }
              }
            ]
          },
          scopeLogs: [
            {
              scope: {},
              logRecords: [
                {
                  timeUnixNano: currentTime.toString(),
                  observedTimeUnixNano: currentTime.toString(),
                  severityText,
                  severityNumber,
                  attributes: [
                    {
                      key: 'vtex.search_index',
                      value: {
                        stringValue: searchIndex
                      }
                    },
                    ...errorAttributes,
                    {
                      key: 'body',
                      value: {
                        stringValue: body
                          ? `${body}`
                          : 'Unexpected error from PAC'
                      }
                    }
                  ],
                  traceId: currentTraceId,
                  spanId
                }
              ]
            }
          ]
        }
      ]
    }

    try {
      if (!isTelemetryEnabled()) {
        const consoleFunction =
          severityText === 'ERROR' ? console.error : console.log
        consoleFunction('Log not sent because telemetry is disabled')
        consoleFunction(data)
        return
      }

      fetch(`${collectorEndpoint}/v1/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
    } catch (error) {
      console.error('Failed to send log:', error)
    }
  }

  async function logError(options: LogOptions): Promise<void> {
    const severityNumber = getSeverityNumber('ERROR')
    await log({ ...options, severityText: 'ERROR', severityNumber })
  }

  async function logInfo(options: LogOptions): Promise<void> {
    const severityNumber = getSeverityNumber('INFO')
    await log({ ...options, severityText: 'INFO', severityNumber })
  }

  async function logWarn(options: LogOptions): Promise<void> {
    const severityNumber = getSeverityNumber('WARN')
    await log({ ...options, severityText: 'WARN', severityNumber })
  }

  async function logDebug(options: LogOptions): Promise<void> {
    const severityNumber = getSeverityNumber('DEBUG')
    await log({ ...options, severityText: 'DEBUG', severityNumber })
  }

  return { log, logError, logInfo, logWarn, logDebug }
}

const telemetryClient = createTelemetryClient()

export { telemetryClient }
